import React, { useState, useEffect, useRef } from "react";

const MolecularStatesSimulation = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const [temperature, setTemperature] = useState(50);
  const [currentState, setCurrentState] = useState("固体");
  const [isHeating, setIsHeating] = useState(false);
  const [showForces, setShowForces] = useState(false);
  const [showShape, setShowShape] = useState(true);
  const [coolingMode, setCoolingMode] = useState(false); // 新增：冷却模式

  // 物理常数
  const CANVAS_WIDTH = 800;
  const CANVAS_HEIGHT = 600;
  const MOLECULE_COUNT = 48;
  const THERMAL_MOLECULE_COUNT = 20; // 重命名：热分子或冷分子数量
  const ATTRACTION_STRENGTH = 0.5;
  const DAMPING = 0.99;

  // 分子类
  class Molecule {
    constructor(x, y, isThermal = false, isCold = false) {
      this.x = x;
      this.y = y;
      this.equilibriumX = x; // 平衡位置
      this.equilibriumY = y;
      this.vx = (Math.random() - 0.5) * 2;
      this.vy = (Math.random() - 0.5) * 2;
      this.fx = 0;
      this.fy = 0;
      this.radius = isThermal ? 3 : 6;
      this.mass = isThermal ? 0.5 : 1;
      this.isThermal = isThermal; // 是热分子或冷分子
      this.isCold = isCold; // 是否为冷分子
      this.energy = 0;
      this.forces = []; // 存储所有作用力，用于可视化
    }

    // 计算与其他分子的引力
    calculateForces(molecules, temp) {
      this.fx = 0;
      this.fy = 0;
      this.forces = []; // 清空力的记录

      if (this.isThermal) {
        // 热分子和冷分子都不受分子间引力影响，就像自由的气体分子
        return;
      }

      // 计算当前物态
      const currentState = temp < 100 ? "solid" : temp < 200 ? "liquid" : "gas";

      // 根据物态调整引力强度
      let attractionStrength = ATTRACTION_STRENGTH;
      let equilibriumDistance = 40;
      let restoreForce = 0;

      if (currentState === "solid") {
        attractionStrength = ATTRACTION_STRENGTH * 3; // 固体中引力更强
        equilibriumDistance = 35; // 更小的平衡距离

        // 固体中分子倾向于回到平衡位置
        const dx_eq = this.equilibriumX - this.x;
        const dy_eq = this.equilibriumY - this.y;
        restoreForce = 0.1; // 回复力系数
        this.fx += dx_eq * restoreForce;
        this.fy += dy_eq * restoreForce;

        // 添加固体的热振动
        const thermalVibration = temp / 100;
        this.fx += (Math.random() - 0.5) * thermalVibration;
        this.fy += (Math.random() - 0.5) * thermalVibration;
      } else if (currentState === "liquid") {
        attractionStrength = ATTRACTION_STRENGTH * 1.8;
        equilibriumDistance = 50;
      } else {
        attractionStrength = ATTRACTION_STRENGTH * 0.3; // 气体中引力很弱
        equilibriumDistance = 80;
      }

      molecules.forEach((other) => {
        if (other === this || other.isThermal) return;

        const dx = other.x - this.x;
        const dy = other.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance > 0) {
          let forceX = 0,
            forceY = 0;

          if (distance < equilibriumDistance) {
            // 距离太近时产生斥力
            const repulsionForce =
              (attractionStrength * 2) / (distance * distance);
            forceX = (-repulsionForce * dx) / distance;
            forceY = (-repulsionForce * dy) / distance;
            this.fx += forceX;
            this.fy += forceY;

            // 记录斥力（红色）
            this.forces.push({
              targetX: other.x,
              targetY: other.y,
              forceX: forceX * 10, // 放大力的显示
              forceY: forceY * 10,
              magnitude: repulsionForce * 10,
              type: "repulsion",
            });
          } else if (distance < equilibriumDistance * 2.5) {
            // 适当距离时产生引力
            const attractionForce =
              (attractionStrength / (distance * distance)) * this.mass;
            forceX = (attractionForce * dx) / distance;
            forceY = (attractionForce * dy) / distance;
            this.fx += forceX;
            this.fy += forceY;

            // 记录引力（蓝色）
            this.forces.push({
              targetX: other.x,
              targetY: other.y,
              forceX: forceX * 10, // 放大力的显示
              forceY: forceY * 10,
              magnitude: attractionForce * 10,
              type: "attraction",
            });
          }
        }
      });
    }

    // 更新位置和速度
    update(temp) {
      const currentState = temp < 100 ? "solid" : temp < 200 ? "liquid" : "gas";

      if (this.isThermal) {
        if (this.isCold) {
          // 冷分子：移动缓慢，轻微随机运动
          const directionNoise = 0.05; // 比热分子更小的随机运动
          this.vx += (Math.random() - 0.5) * directionNoise;
          this.vy += (Math.random() - 0.5) * directionNoise;

          // 冷分子整体速度较慢
          const maxColdSpeed = 2.0;
          const speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
          if (speed > maxColdSpeed) {
            this.vx = (this.vx / speed) * maxColdSpeed;
            this.vy = (this.vy / speed) * maxColdSpeed;
          }
        } else {
          // 热分子：不受阻尼影响，保持高速运动
          const directionNoise = 0.1;
          this.vx += (Math.random() - 0.5) * directionNoise;
          this.vy += (Math.random() - 0.5) * directionNoise;
        }
      } else {
        // 普通分子受力运动
        this.vx += this.fx / this.mass;
        this.vy += this.fy / this.mass;

        // 根据物态调整运动特性
        let thermalNoise, maxSpeed, dampingFactor;

        if (currentState === "solid") {
          thermalNoise = temp / 800; // 固体中热运动很小
          maxSpeed = 2.0; // 固体中分子运动速度限制
          dampingFactor = 0.92; // 强阻尼
        } else if (currentState === "liquid") {
          thermalNoise = temp / 400; // 液体中热运动适中
          maxSpeed = temp / 20; // 液体中运动相对自由
          dampingFactor = 0.96; // 中等阻尼
        } else {
          thermalNoise = temp / 200; // 气体中热运动强
          maxSpeed = temp / 10; // 气体中运动很自由
          dampingFactor = 0.99; // 很小阻尼
        }

        this.vx += (Math.random() - 0.5) * thermalNoise;
        this.vy += (Math.random() - 0.5) * thermalNoise;

        // 限制最大速度
        const speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
        if (speed > maxSpeed) {
          this.vx = (this.vx / speed) * maxSpeed;
          this.vy = (this.vy / speed) * maxSpeed;
        }

        // 应用阻尼
        this.vx *= dampingFactor;
        this.vy *= dampingFactor;
      }

      // 更新位置
      this.x += this.vx;
      this.y += this.vy;

      // 普通分子的边界约束
      if (!this.isThermal) {
        const margin = 50;
        if (this.x < margin) {
          this.x = margin;
          this.vx = Math.abs(this.vx) * 0.8;
        }
        if (this.x > CANVAS_WIDTH - margin) {
          this.x = CANVAS_WIDTH - margin;
          this.vx = -Math.abs(this.vx) * 0.8;
        }
        if (this.y < margin) {
          this.y = margin;
          this.vy = Math.abs(this.vy) * 0.8;
        }
        if (this.y > CANVAS_HEIGHT - margin) {
          this.y = CANVAS_HEIGHT - margin;
          this.vy = -Math.abs(this.vy) * 0.8;
        }
      }
      // 热分子和冷分子没有边界约束，可以自由离开
    }

    // 检测与热分子/冷分子的碰撞
    checkThermalCollision(thermalMolecules) {
      thermalMolecules.forEach((thermal) => {
        const dx = thermal.x - this.x;
        const dy = thermal.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < this.radius + thermal.radius) {
          if (thermal.isCold) {
            // 与冷分子碰撞：普通分子失去动能（变冷）
            const energyTransfer = 0.6; // 冷分子吸收更多热量
            thermal.vx += this.vx * energyTransfer * 0.3; // 冷分子只获得少量动能
            thermal.vy += this.vy * energyTransfer * 0.3;

            // 普通分子大幅减速（失去热量）
            this.vx *= 1 - energyTransfer;
            this.vy *= 1 - energyTransfer;
          } else {
            // 与热分子碰撞：普通分子获得动能（变热）
            const energyTransfer = 0.4;
            this.vx += thermal.vx * energyTransfer;
            this.vy += thermal.vy * energyTransfer;

            // 热分子碰撞后速度减小（失去能量）
            thermal.vx *= 0.3;
            thermal.vy *= 0.3;
          }

          // 反弹
          const overlap = this.radius + thermal.radius - distance;
          if (distance > 0) {
            const separationX = (dx / distance) * overlap * 0.5;
            const separationY = (dy / distance) * overlap * 0.5;

            this.x -= separationX;
            this.y -= separationY;
            thermal.x += separationX;
            thermal.y += separationY;
          }
        }
      });
    }

    // 热分子/冷分子管理
    static manageThermalMolecules(thermalMolecules, temp, isCoolingMode) {
      const CANVAS_W = 800; // 使用局部常量
      const CANVAS_H = 600;
      const THERMAL_COUNT = 20;

      // 移除速度过慢或离开边界的热分子/冷分子
      for (let i = thermalMolecules.length - 1; i >= 0; i--) {
        const thermal = thermalMolecules[i];
        const speed = Math.sqrt(
          thermal.vx * thermal.vx + thermal.vy * thermal.vy
        );

        const minSpeed = isCoolingMode ? 0.5 : temp / 20; // 冷分子的最低速度要求更低

        // 如果速度太慢或者离开了边界区域，就移除
        if (
          speed < minSpeed ||
          thermal.x < -50 ||
          thermal.x > CANVAS_W + 50 ||
          thermal.y < -50 ||
          thermal.y > CANVAS_H + 50
        ) {
          thermalMolecules.splice(i, 1);
        }
      }

      // 补充新的热分子/冷分子，保持数量
      while (thermalMolecules.length < THERMAL_COUNT) {
        const newThermal = Molecule.createNewThermalMolecule(
          temp,
          isCoolingMode
        );
        thermalMolecules.push(newThermal);
      }
    }

    // 创建新的热分子/冷分子
    static createNewThermalMolecule(temp, isCoolingMode) {
      const CANVAS_W = 800; // 使用局部常量
      const CANVAS_H = 600;

      let x, y, vx, vy;

      if (isCoolingMode) {
        // 冷分子：速度较慢
        const speed = 1.5 + Math.random() * 2; // 冷分子速度范围：1.5-3.5

        // 从四个边界随机选择一个
        const side = Math.floor(Math.random() * 4);

        switch (side) {
          case 0: // 上边
            x = Math.random() * CANVAS_W;
            y = -20;
            vx = (Math.random() - 0.5) * speed;
            vy = Math.abs(Math.random() * speed); // 向下
            break;
          case 1: // 右边
            x = CANVAS_W + 20;
            y = Math.random() * CANVAS_H;
            vx = -Math.abs(Math.random() * speed); // 向左
            vy = (Math.random() - 0.5) * speed;
            break;
          case 2: // 下边
            x = Math.random() * CANVAS_W;
            y = CANVAS_H + 20;
            vx = (Math.random() - 0.5) * speed;
            vy = -Math.abs(Math.random() * speed); // 向上
            break;
          case 3: // 左边
            x = -20;
            y = Math.random() * CANVAS_H;
            vx = Math.abs(Math.random() * speed); // 向右
            vy = (Math.random() - 0.5) * speed;
            break;
        }

        const coldMolecule = new Molecule(x, y, true, true); // isThermal=true, isCold=true
        coldMolecule.vx = vx;
        coldMolecule.vy = vy;
        return coldMolecule;
      } else {
        // 热分子：速度较快（原有逻辑）
        const speed = temp / 8 + (Math.random() * temp) / 12; // 随机速度

        // 从四个边界随机选择一个
        const side = Math.floor(Math.random() * 4);

        switch (side) {
          case 0: // 上边
            x = Math.random() * CANVAS_W;
            y = -20;
            vx = (Math.random() - 0.5) * speed;
            vy = Math.abs(Math.random() * speed); // 向下
            break;
          case 1: // 右边
            x = CANVAS_W + 20;
            y = Math.random() * CANVAS_H;
            vx = -Math.abs(Math.random() * speed); // 向左
            vy = (Math.random() - 0.5) * speed;
            break;
          case 2: // 下边
            x = Math.random() * CANVAS_W;
            y = CANVAS_H + 20;
            vx = (Math.random() - 0.5) * speed;
            vy = -Math.abs(Math.random() * speed); // 向上
            break;
          case 3: // 左边
            x = -20;
            y = Math.random() * CANVAS_H;
            vx = Math.abs(Math.random() * speed); // 向右
            vy = (Math.random() - 0.5) * speed;
            break;
        }

        const hotMolecule = new Molecule(x, y, true, false); // isThermal=true, isCold=false
        hotMolecule.vx = vx;
        hotMolecule.vy = vy;
        return hotMolecule;
      }
    }
  }

  // 初始化分子
  const initializeMolecules = (temp, isCoolingMode = false) => {
    const molecules = [];
    const thermalMolecules = [];

    // 根据温度确定初始排列
    if (temp < 100) {
      // 固体 - 非常紧密的规整排列
      const cols = 8;
      const rows = 6;
      const spacing = 40; // 减小间距，让分子更紧密
      const startX = CANVAS_WIDTH / 2 - (cols * spacing) / 2;
      const startY = CANVAS_HEIGHT / 2 - (rows * spacing) / 2;

      for (let i = 0; i < rows; i++) {
        for (let j = 0; j < cols; j++) {
          const x = startX + j * spacing + (Math.random() - 0.5) * 3; // 减小随机偏移
          const y = startY + i * spacing + (Math.random() - 0.5) * 3;
          const molecule = new Molecule(x, y);
          // 设置平衡位置为初始位置
          molecule.equilibriumX = startX + j * spacing;
          molecule.equilibriumY = startY + i * spacing;
          molecules.push(molecule);
        }
      }
    } else if (temp < 200) {
      // 液体 - 紧密但不规整，形成液滴状
      const centerX = CANVAS_WIDTH / 2;
      const centerY = CANVAS_HEIGHT / 2;
      const baseRadius = 100; // 液体基础半径

      // 使用多层圆形排列，模拟液体的流动性
      const layers = 6;
      const moleculesPerLayer = Math.ceil(MOLECULE_COUNT / layers);

      for (let layer = 0; layer < layers; layer++) {
        const radius = (baseRadius * (layer + 1)) / layers;
        const countInLayer = Math.min(
          moleculesPerLayer,
          MOLECULE_COUNT - layer * moleculesPerLayer
        );

        for (let i = 0; i < countInLayer; i++) {
          const angle = (i / countInLayer) * 2 * Math.PI + Math.random() * 0.5;
          const r = radius * (0.7 + Math.random() * 0.6); // 随机半径变化
          const x = centerX + r * Math.cos(angle) + (Math.random() - 0.5) * 25;
          const y = centerY + r * Math.sin(angle) + (Math.random() - 0.5) * 25;
          const molecule = new Molecule(x, y);
          molecule.equilibriumX = x; // 液体没有固定平衡位置
          molecule.equilibriumY = y;
          molecules.push(molecule);

          if (molecules.length >= MOLECULE_COUNT) break;
        }
        if (molecules.length >= MOLECULE_COUNT) break;
      }
    } else {
      // 气体 - 完全随机分布，充满整个容器
      for (let i = 0; i < MOLECULE_COUNT; i++) {
        const x = 80 + Math.random() * (CANVAS_WIDTH - 160);
        const y = 80 + Math.random() * (CANVAS_HEIGHT - 160);
        const molecule = new Molecule(x, y);
        molecule.equilibriumX = x;
        molecule.equilibriumY = y;
        // 给气体分子初始随机速度
        molecule.vx = (Math.random() - 0.5) * 8;
        molecule.vy = (Math.random() - 0.5) * 8;
        molecules.push(molecule);
      }
    }

    // 创建热分子或冷分子，分布在四周
    for (let i = 0; i < THERMAL_MOLECULE_COUNT; i++) {
      const newThermal = Molecule.createNewThermalMolecule(temp, isCoolingMode);
      thermalMolecules.push(newThermal);
    }

    return { molecules, thermalMolecules };
  };

  // 计算平均分子间距
  const calculateAverageDistance = (molecules) => {
    let totalDistance = 0;
    let count = 0;

    for (let i = 0; i < molecules.length; i++) {
      for (let j = i + 1; j < molecules.length; j++) {
        const dx = molecules[i].x - molecules[j].x;
        const dy = molecules[i].y - molecules[j].y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        totalDistance += distance;
        count++;
      }
    }

    return count > 0 ? totalDistance / count : 0;
  };

  // 找出聚集的分子群（排除离群分子）
  const findClusteredMolecules = (molecules, currentState) => {
    if (currentState === "气体") return [];

    // 根据状态设置聚集阈值
    const clusterDistance = currentState === "固体" ? 60 : 80;
    const minClusterSize = Math.floor(molecules.length * 0.6); // 至少60%的分子才算主要群体

    // 计算每个分子的邻居数量
    const moleculeNeighbors = molecules.map((mol) => {
      let neighbors = 0;
      molecules.forEach((other) => {
        if (other === mol) return;
        const dx = other.x - mol.x;
        const dy = other.y - mol.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < clusterDistance) neighbors++;
      });
      return { molecule: mol, neighbors };
    });

    // 找出有足够邻居的分子（密集区域）
    const clusteredMolecules = moleculeNeighbors
      .filter((item) => item.neighbors >= 2) // 至少有2个邻居
      .map((item) => item.molecule);

    // 如果聚集分子数量太少，返回全部分子
    if (clusteredMolecules.length < minClusterSize) {
      return molecules;
    }

    return clusteredMolecules;
  };

  // 计算凸包（Graham扫描算法的简化版）
  const calculateConvexHull = (points) => {
    if (points.length < 3) return points;

    // 找最下面的点（y最小，如果相同则x最小）
    let bottomPoint = points[0];
    for (let i = 1; i < points.length; i++) {
      if (
        points[i].y < bottomPoint.y ||
        (points[i].y === bottomPoint.y && points[i].x < bottomPoint.x)
      ) {
        bottomPoint = points[i];
      }
    }

    // 按极角排序
    const sortedPoints = points
      .filter((p) => p !== bottomPoint)
      .sort((a, b) => {
        const angleA = Math.atan2(a.y - bottomPoint.y, a.x - bottomPoint.x);
        const angleB = Math.atan2(b.y - bottomPoint.y, b.x - bottomPoint.x);
        return angleA - angleB;
      });

    const hull = [bottomPoint];

    for (let point of sortedPoints) {
      // 移除不构成左转的点
      while (hull.length > 1) {
        const p1 = hull[hull.length - 2];
        const p2 = hull[hull.length - 1];
        const cross =
          (p2.x - p1.x) * (point.y - p1.y) - (p2.y - p1.y) * (point.x - p1.x);
        if (cross > 0) break; // 左转，保留
        hull.pop(); // 右转或共线，移除
      }
      hull.push(point);
    }

    return hull;
  };

  // 平滑化边界点
  const smoothBoundary = (points, smoothness = 0.3) => {
    if (points.length < 3) return points;

    const smoothed = [];
    for (let i = 0; i < points.length; i++) {
      const prev = points[(i - 1 + points.length) % points.length];
      const curr = points[i];
      const next = points[(i + 1) % points.length];

      // 使用加权平均来平滑点
      smoothed.push({
        x: curr.x * (1 - smoothness) + ((prev.x + next.x) * smoothness) / 2,
        y: curr.y * (1 - smoothness) + ((prev.y + next.y) * smoothness) / 2,
      });
    }
    return smoothed;
  };

  // 计算分子聚集区域（改进版）
  const calculateMolecularShape = (molecules, currentState) => {
    if (currentState === "气体") return null;

    // 找出聚集的分子，排除离群分子
    const clusteredMolecules = findClusteredMolecules(molecules, currentState);
    if (clusteredMolecules.length < 3) return null;

    if (currentState === "固体") {
      // 固体：创建包围所有分子的圆润矩形
      const xs = clusteredMolecules.map((m) => m.x);
      const ys = clusteredMolecules.map((m) => m.y);
      const minX = Math.min(...xs) - 20;
      const maxX = Math.max(...xs) + 20;
      const minY = Math.min(...ys) - 20;
      const maxY = Math.max(...ys) + 20;

      // 创建更圆润的矩形边界
      const cornerRadius = 25;
      const points = [];
      const resolution = 8; // 每个圆角的点数

      // 右上角
      for (let i = 0; i <= resolution; i++) {
        const angle = -Math.PI / 2 + ((Math.PI / 2) * i) / resolution;
        points.push({
          x: maxX - cornerRadius + cornerRadius * Math.cos(angle),
          y: minY + cornerRadius + cornerRadius * Math.sin(angle),
        });
      }

      // 右下角
      for (let i = 0; i <= resolution; i++) {
        const angle = 0 + ((Math.PI / 2) * i) / resolution;
        points.push({
          x: maxX - cornerRadius + cornerRadius * Math.cos(angle),
          y: maxY - cornerRadius + cornerRadius * Math.sin(angle),
        });
      }

      // 左下角
      for (let i = 0; i <= resolution; i++) {
        const angle = Math.PI / 2 + ((Math.PI / 2) * i) / resolution;
        points.push({
          x: minX + cornerRadius + cornerRadius * Math.cos(angle),
          y: maxY - cornerRadius + cornerRadius * Math.sin(angle),
        });
      }

      // 左上角
      for (let i = 0; i <= resolution; i++) {
        const angle = Math.PI + ((Math.PI / 2) * i) / resolution;
        points.push({
          x: minX + cornerRadius + cornerRadius * Math.cos(angle),
          y: minY + cornerRadius + cornerRadius * Math.sin(angle),
        });
      }

      return points;
    } else {
      // 液体：创建自然的不规则形状
      // 在分子位置周围生成边界点
      const boundaryPoints = [];
      const boundaryDistance = 35; // 边界距离

      clusteredMolecules.forEach((mol) => {
        // 在每个分子周围生成多个边界点
        for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 4) {
          boundaryPoints.push({
            x: mol.x + Math.cos(angle) * boundaryDistance,
            y: mol.y + Math.sin(angle) * boundaryDistance,
          });
        }
      });

      // 计算凸包
      let hull = calculateConvexHull(boundaryPoints);

      // 多次平滑化处理，让形状更自然
      hull = smoothBoundary(hull, 0.4);
      hull = smoothBoundary(hull, 0.2);

      return hull;
    }
  };

  // 绘制分子聚集区域（改进版 - 使用平滑曲线）
  const drawMolecularShape = (ctx, shape, currentState) => {
    if (!shape || shape.length < 3) return;

    // 根据状态设置颜色
    let fillColor, strokeColor;
    if (currentState === "固体") {
      fillColor = "rgba(100, 150, 255, 0.2)"; // 蓝色，较深
      strokeColor = "rgba(100, 150, 255, 0.4)";
    } else if (currentState === "液体") {
      fillColor = "rgba(120, 200, 255, 0.15)"; // 蓝色，较浅
      strokeColor = "rgba(120, 200, 255, 0.3)";
    }

    ctx.fillStyle = fillColor;
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 2;

    // 使用平滑曲线绘制
    ctx.beginPath();

    if (currentState === "固体") {
      // 固体：直接连接圆角点（已经很圆润了）
      ctx.moveTo(shape[0].x, shape[0].y);
      for (let i = 1; i < shape.length; i++) {
        ctx.lineTo(shape[i].x, shape[i].y);
      }
    } else {
      // 液体：使用贝塞尔曲线让形状更自然流畅
      ctx.moveTo(shape[0].x, shape[0].y);

      for (let i = 0; i < shape.length; i++) {
        const current = shape[i];
        const next = shape[(i + 1) % shape.length];
        const nextNext = shape[(i + 2) % shape.length];

        // 计算控制点
        const cpX = next.x + (nextNext.x - current.x) * 0.1;
        const cpY = next.y + (nextNext.y - current.y) * 0.1;

        ctx.quadraticCurveTo(cpX, cpY, next.x, next.y);
      }
    }

    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  };

  // 修复：只根据温度判断物质状态
  const determineState = (temp) => {
    if (temp < 100) return "固体";
    if (temp < 200) return "液体";
    return "气体";
  };

  const moleculesRef = useRef(null);
  const thermalMoleculesRef = useRef(null);

  useEffect(() => {
    const { molecules, thermalMolecules } = initializeMolecules(
      temperature,
      coolingMode
    );
    moleculesRef.current = molecules;
    thermalMoleculesRef.current = thermalMolecules;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const animate = () => {
      ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      const molecules = moleculesRef.current;
      const thermalMolecules = thermalMoleculesRef.current;

      if (!molecules || !thermalMolecules) return;

      // 更新分子
      molecules.forEach((mol) => {
        mol.calculateForces(molecules, temperature);
        mol.checkThermalCollision(thermalMolecules);
        mol.update(temperature);
      });

      thermalMolecules.forEach((thermal) => {
        thermal.update(temperature);
      });

      // 管理热分子/冷分子：移除慢速/离界的，补充新的
      Molecule.manageThermalMolecules(
        thermalMolecules,
        temperature,
        coolingMode
      );

      // 修复：直接用温度判断状态，而不是用平均距离
      const newState = determineState(temperature);
      setCurrentState(newState);

      // 绘制分子聚集区域（阴影）- 在所有其他内容之前绘制
      if (showShape && (newState === "固体" || newState === "液体")) {
        const molecularShape = calculateMolecularShape(molecules, newState);
        if (molecularShape) {
          drawMolecularShape(ctx, molecularShape, newState);
        }
      }

      // 绘制分子间作用力可视化
      if (showForces) {
        molecules.forEach((mol) => {
          if (mol.forces && mol.forces.length > 0) {
            mol.forces.forEach((force) => {
              // 确保力的大小足够显示
              const forceMagnitude = Math.max(force.magnitude, 0.01);
              const alpha = Math.min(forceMagnitude * 2, 0.8); // 增加可见度

              if (force.type === "attraction") {
                // 绘制引力箭头（蓝色）
                ctx.strokeStyle = `rgba(0, 100, 255, ${alpha})`;
                ctx.fillStyle = `rgba(0, 100, 255, ${alpha})`;
              } else {
                // 绘制斥力箭头（红色）
                ctx.strokeStyle = `rgba(255, 50, 50, ${alpha})`;
                ctx.fillStyle = `rgba(255, 50, 50, ${alpha})`;
              }

              ctx.lineWidth = 2;

              // 计算箭头方向和长度
              const forceLength = Math.sqrt(
                force.forceX * force.forceX + force.forceY * force.forceY
              );
              if (forceLength > 0) {
                // 标准化力的方向
                const normalizedFx = force.forceX / forceLength;
                const normalizedFy = force.forceY / forceLength;

                // 箭头长度固定为25像素，便于观察
                const arrowLength = 25;
                const endX = mol.x + normalizedFx * arrowLength;
                const endY = mol.y + normalizedFy * arrowLength;

                // 绘制箭头主体
                ctx.beginPath();
                ctx.moveTo(mol.x, mol.y);
                ctx.lineTo(endX, endY);
                ctx.stroke();

                // 绘制箭头头部
                const angle = Math.atan2(normalizedFy, normalizedFx);
                const arrowHeadLength = 8;
                const arrowHeadAngle = Math.PI / 6;

                ctx.beginPath();
                ctx.moveTo(endX, endY);
                ctx.lineTo(
                  endX - arrowHeadLength * Math.cos(angle - arrowHeadAngle),
                  endY - arrowHeadLength * Math.sin(angle - arrowHeadAngle)
                );
                ctx.moveTo(endX, endY);
                ctx.lineTo(
                  endX - arrowHeadLength * Math.cos(angle + arrowHeadAngle),
                  endY - arrowHeadLength * Math.sin(angle + arrowHeadAngle)
                );
                ctx.stroke();
              }
            });
          }
        });
      }

      // 绘制分子间连线（表示分子间作用力）
      if (currentState !== "气体" && !showForces) {
        ctx.strokeStyle =
          currentState === "固体"
            ? "rgba(150, 150, 150, 0.4)"
            : "rgba(100, 100, 100, 0.2)";
        ctx.lineWidth = currentState === "固体" ? 2 : 1;
        molecules.forEach((mol1, i) => {
          molecules.forEach((mol2, j) => {
            if (i >= j) return;
            const dx = mol2.x - mol1.x;
            const dy = mol2.y - mol1.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            const maxDistance = currentState === "固体" ? 50 : 70; // 固体中连线距离更短
            if (distance < maxDistance) {
              ctx.beginPath();
              ctx.moveTo(mol1.x, mol1.y);
              ctx.lineTo(mol2.x, mol2.y);
              ctx.stroke();
            }
          });
        });
      }

      // 绘制普通分子
      molecules.forEach((mol) => {
        const speed = Math.sqrt(mol.vx * mol.vx + mol.vy * mol.vy);
        const intensity = Math.min(speed * 10, 255);

        ctx.beginPath();
        ctx.arc(mol.x, mol.y, mol.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgb(${50 + intensity}, ${150 - intensity / 2}, 255)`;
        ctx.fill();
        ctx.strokeStyle = "#333";
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      // 绘制热分子/冷分子
      thermalMolecules.forEach((thermal) => {
        ctx.beginPath();
        ctx.arc(thermal.x, thermal.y, thermal.radius, 0, Math.PI * 2);

        if (thermal.isCold) {
          // 冷分子：蓝色
          ctx.fillStyle = "#4488ff";
          ctx.fill();

          // 冷分子的冷气效果（蓝色光晕）
          const gradient = ctx.createRadialGradient(
            thermal.x,
            thermal.y,
            0,
            thermal.x,
            thermal.y,
            12
          );
          gradient.addColorStop(0, "rgba(100, 150, 255, 0.4)");
          gradient.addColorStop(1, "rgba(100, 150, 255, 0)");
          ctx.fillStyle = gradient;
          ctx.fill();
        } else {
          // 热分子：红色
          ctx.fillStyle = "#ff4444";
          ctx.fill();

          // 热分子的光晕效果
          const gradient = ctx.createRadialGradient(
            thermal.x,
            thermal.y,
            0,
            thermal.x,
            thermal.y,
            15
          );
          gradient.addColorStop(0, "rgba(255, 100, 100, 0.6)");
          gradient.addColorStop(1, "rgba(255, 100, 100, 0)");
          ctx.fillStyle = gradient;
          ctx.fill();
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [temperature, showForces, showShape, coolingMode]); // 添加 coolingMode 依赖

  const handleTemperatureChange = (newTemp) => {
    setTemperature(newTemp);
    // 重新初始化分子排列
    if (newTemp < 50) {
      const { molecules, thermalMolecules } = initializeMolecules(
        newTemp,
        coolingMode
      );
      moleculesRef.current = molecules;
      thermalMoleculesRef.current = thermalMolecules;
    }
  };

  const handleModeChange = (isCooling) => {
    setCoolingMode(isCooling);
    // 立即重新初始化热分子/冷分子
    const { molecules, thermalMolecules } = initializeMolecules(
      temperature,
      isCooling
    );
    moleculesRef.current = molecules;
    thermalMoleculesRef.current = thermalMolecules;
  };

  const getStateColor = (state) => {
    switch (state) {
      case "固体":
        return "#4CAF50";
      case "液体":
        return "#2196F3";
      case "气体":
        return "#FF9800";
      default:
        return "#666";
    }
  };

  const getStateDescription = (state) => {
    switch (state) {
      case "固体":
        return "分子排列紧密规整，只能在固定位置振动";
      case "液体":
        return "分子间距适中，可以流动但仍有一定约束";
      case "气体":
        return "分子间距很大，运动自由，填满整个容器";
      default:
        return "";
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-6 bg-gray-50 rounded-lg">
      <h1 className="text-3xl font-bold text-center mb-6 text-gray-800">
        物质三态分子运动模拟
      </h1>

      <div className="mb-6 text-center">
        <div
          className="inline-block p-4 rounded-lg"
          style={{ backgroundColor: getStateColor(currentState) + "20" }}
        >
          <h2
            className="text-2xl font-bold mb-2"
            style={{ color: getStateColor(currentState) }}
          >
            当前状态：{currentState}
          </h2>
          <p className="text-gray-600">{getStateDescription(currentState)}</p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1">
          <canvas
            ref={canvasRef}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            className="border-2 border-gray-300 rounded-lg bg-white shadow-lg"
            style={{ maxWidth: "100%", height: "auto" }}
          />
        </div>

        <div className="lg:w-80 space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold mb-4">温度控制</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  温度: {temperature}°C
                </label>
                <input
                  type="range"
                  min="10"
                  max="300"
                  value={temperature}
                  onChange={(e) =>
                    handleTemperatureChange(parseInt(e.target.value))
                  }
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => handleTemperatureChange(30)}
                  className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                >
                  固体
                </button>
                <button
                  onClick={() => handleTemperatureChange(120)}
                  className="px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                >
                  液体
                </button>
                <button
                  onClick={() => handleTemperatureChange(250)}
                  className="px-3 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 text-sm"
                >
                  气体
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    模式选择
                  </label>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleModeChange(false)}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        !coolingMode
                          ? "bg-red-500 text-white"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      🔥 加热模式
                    </button>
                    <button
                      onClick={() => handleModeChange(true)}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        coolingMode
                          ? "bg-blue-500 text-white"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      ❄️ 冷却模式
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {coolingMode
                      ? "冷分子吸收热量，使物质冷却凝聚"
                      : "热分子提供热量，使物质加热膨胀"}
                  </p>
                </div>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={showForces}
                    onChange={(e) => setShowForces(e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    显示分子间作用力
                  </span>
                </label>
                <p className="text-xs text-gray-500 ml-6">
                  蓝色箭头：引力 | 红色箭头：斥力
                </p>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={showShape}
                    onChange={(e) => setShowShape(e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    显示物体形状
                  </span>
                </label>
                <p className="text-xs text-gray-500 ml-6">
                  固体：深蓝阴影，圆润边角 | 液体：浅蓝阴影，自然不规则形状
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold mb-4">模拟说明</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-blue-400 rounded-full"></div>
                <span>普通分子 - 颜色表示运动速度</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span>热分子 - 撞击提供热量（加热模式）</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                <span>冷分子 - 吸收热量降温（冷却模式）</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-0.5 bg-gray-400"></div>
                <span>分子间作用力连线</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-3 h-0.5 bg-blue-500"></div>
                <span>引力箭头（蓝色）</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-3 h-0.5 bg-red-500"></div>
                <span>斥力箭头（红色）</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-4 bg-blue-200 border border-blue-300 rounded"></div>
                <span>物体形状阴影</span>
              </div>
            </div>
            <div className="mt-4 p-3 bg-gray-50 rounded text-xs">
              <p>
                <strong>观察要点：</strong>
              </p>
              <p>• 固体：分子有轻微抖动，排列整齐，圆润固定形状</p>
              <p>• 液体：聚集分子形成自然流动形状，离群分子被排除</p>
              <p>• 气体：分子分散运动，引力很弱，无固定形状</p>
              <p>
                <strong>加热模式：</strong>红色热分子从四周撞入，传递热量后离开
              </p>
              <p>
                <strong>冷却模式：</strong>
                蓝色冷分子缓慢接近，吸收热量使分子冷却凝聚
              </p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold mb-4">物理原理</h3>
            <div className="text-sm text-gray-600 space-y-2">
              <p>• 分子间存在引力（范德华力）和斥力</p>
              <p>• 温度反映分子平均动能</p>
              <p>• 相变时能量主要用于克服分子间作用力</p>
              <p>• 物态取决于分子间作用力与热运动的平衡</p>
              <p>• 加热模式：热分子提供外部能量，增加分子运动</p>
              <p>• 冷却模式：冷分子吸收热量，促进分子聚集和相变</p>
            </div>
          </div>
        </div>
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default MolecularStatesSimulation;
